'use client';

import { useState, useRef, useEffect } from 'react';
import {
  ReviewsContainer,
  ScrollContainer,
  ReviewItem,
  Quote,
  Name,
  // Profile,
  DotsContainer,
  Dot,
} from './StyledConstants';

export const Reviews = ({
  reviews,
  autoScrollInterval = 5000, // Default 5 second interval
  pauseOnHover = true, // Option to pause on hover
}: {
  reviews: { quote: string; name: string; profile: string }[];
  autoScrollInterval?: number;
  pauseOnHover?: boolean;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<NodeJS.Timeout | null>(null);

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollPosition = scrollRef.current.scrollLeft;
      const itemWidth = scrollRef.current.offsetWidth;
      const newIndex = Math.round(scrollPosition / itemWidth);
      setActiveIndex(newIndex);
    }
  };

  const scrollToReview = (index: number) => {
    if (scrollRef.current) {
      const itemWidth = scrollRef.current.offsetWidth;
      scrollRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth',
      });
    }
  };

  const startAutoScroll = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }

    autoScrollRef.current = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (!isPaused) {
        const nextIndex = (activeIndex + 1) % reviews.length;
        scrollToReview(nextIndex);
        setActiveIndex(nextIndex);
      }
    }, autoScrollInterval);
  };

  // Set up scroll event listener and auto-scroll
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      startAutoScroll();

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
        if (autoScrollRef.current) {
          clearInterval(autoScrollRef.current);
        }
      };
    }
  }, [activeIndex, isPaused, reviews.length, autoScrollInterval]);

  // Handle mouse enter/leave for pause on hover
  const handleMouseEnter = () => {
    if (pauseOnHover) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (pauseOnHover) {
      setIsPaused(false);
    }
  };

  return (
    <ReviewsContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ScrollContainer ref={scrollRef}>
        {reviews.map((review, index) => (
          <ReviewItem key={`review-${index}`}>
            <Quote>{review.quote}</Quote>
            <Name>{review.name}</Name>
            {/* <Profile>{review.profile}</Profile> */}
          </ReviewItem>
        ))}
      </ScrollContainer>
      <DotsContainer>
        {reviews.map((_, index) => (
          <Dot
            key={`dot-${index}`}
            isActive={index === activeIndex}
            onClick={() => {
              scrollToReview(index);
              setActiveIndex(index);
            }}
            aria-label={`Go to review ${index + 1}`}
          />
        ))}
      </DotsContainer>
    </ReviewsContainer>
  );
};
